import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Bot } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { getDraftPrompts } from '@/api/prompts'
import { Banner } from '@/components/Banner'
import { STALE_DRAFT_SAVE_REASON, StaleDraftBanner } from '@/components/StaleDraftBanner'
import { ConfirmModal } from '@/components/ConfirmModal'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import type { SelectOption } from '@/components/Select'
import { Tooltip } from '@/components/Tooltip'
import { useServerSeed } from '@/hooks/useServerSeed'
import { errorMessage, formatSaveError } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { ExtractorEntry, ExtractorsFile } from '@/types/bundle'
import { ExtractorForm } from './components/ExtractorRow'
import { getExtractorColumns, type ExtractorRow } from './components/extractorColumns'
import { NewOrDuplicateExtractorModal } from './components/NewOrDuplicateExtractorModal'

function SkeletonBlock() {
  return <div className="h-48 animate-pulse rounded-control bg-surface-raised" />
}

const QUERY_KEY = ['draft-file', 'extractors']

// List + inline accordion editing over extractors.json - mirrors Lookup
// Table's own list+accordion screen structurally, but keyed by a real,
// stable extractor id (like product_types/document_types) rather than a
// client-only localKey, since extractors.json's own keys are already real
// and JSON object keys can't literally duplicate (New/Duplicate collect a
// fresh id via a modal, same as Product Types' own pattern).
export function ExtractorsScreen() {
  const queryClient = useQueryClient()
  const extractorsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => getDraftFile<ExtractorsFile>('extractors'),
  })
  // For the Prompt template file dropdown below - shares its cache with the
  // Prompts screen (same query key), per PRD 00's own convention.
  const draftPromptsQuery = useQuery({
    queryKey: ['draft-prompts'],
    queryFn: () => getDraftPrompts(),
  })
  const promptFileOptions: SelectOption[] = (draftPromptsQuery.data ?? []).map((entry) => ({
    value: entry.filename,
    label: entry.filename,
  }))

  const [draft, setDraft] = useState<ExtractorsFile | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<ExtractorsFile | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [duplicateSource, setDuplicateSource] = useState<{ id: string; extractor: ExtractorEntry } | undefined>(
    undefined,
  )
  const [deleteTarget, setDeleteTarget] = useState<ExtractorRow | null>(null)

  // Seeds the editable draft and follows server changes (hooks/useServerSeed.ts).
  const serverSeed = useServerSeed(QUERY_KEY, extractorsQuery, {
    seed: (data) => {
      setDraft(data)
      setSavedSnapshot(data)
    },
    isDirty: () => JSON.stringify(draft) !== JSON.stringify(savedSnapshot),
  })

  const saveMutation = useMutation({
    mutationFn: (payload: ExtractorsFile) => putDraftFile<ExtractorsFile>('extractors', payload),
    onSuccess: (_result, payload) => {
      setDraft(payload)
      setSavedSnapshot(payload)
      serverSeed.noteSaved(payload)
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'extractors' })
    },
  })

  if (extractorsQuery.isPending) {
    return <SkeletonBlock />
  }

  if (extractorsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load extractors">
        {errorMessage(extractorsQuery.error)}
      </Banner>
    )
  }

  if (!draft || !savedSnapshot) {
    return <SkeletonBlock />
  }

  const rows: ExtractorRow[] = Object.entries(draft.extractors).map(([id, extractor]) => ({ id, extractor }))
  const isDirty = JSON.stringify(draft) !== JSON.stringify(savedSnapshot)
  const hasEmptyModelId = rows.some((row) => row.extractor.model_id.trim().length === 0)

  function updateExtractor(id: string, updater: (extractor: ExtractorEntry) => ExtractorEntry) {
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        extractors: {
          ...current.extractors,
          [id]: updater(current.extractors[id]),
        },
      }
    })
  }

  function openNewModal() {
    setDuplicateSource(undefined)
    setModalOpen(true)
  }

  function openDuplicateModal(row: ExtractorRow) {
    setDuplicateSource({ id: row.id, extractor: row.extractor })
    setModalOpen(true)
  }

  function handleModalConfirm(newId: string, initialEntry: ExtractorEntry) {
    setDraft((current) => {
      if (!current) return current
      return { ...current, extractors: { ...current.extractors, [newId]: initialEntry } }
    })
    setExpandedId(newId)
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return
    setDraft((current) => {
      if (!current) return current
      const extractors = { ...current.extractors }
      delete extractors[deleteTarget.id]
      return { ...current, extractors }
    })
    setExpandedId((current) => (current === deleteTarget.id ? null : current))
    setDeleteTarget(null)
  }

  async function handleSave() {
    if (!draft || hasEmptyModelId || !(await serverSeed.ensureCurrent())) return
    saveMutation.mutate(draft)
  }

  const columns = getExtractorColumns({
    expandedId,
    onToggleExpand: (id) => setExpandedId((current) => (current === id ? null : id)),
    onDuplicate: openDuplicateModal,
    onDelete: setDeleteTarget,
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-title font-semibold text-text-primary">Extractors</h1>
        <button
          type="button"
          onClick={openNewModal}
          className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
        >
          + New Extractor
        </button>
      </div>

      {serverSeed.baseChanged && <StaleDraftBanner onReload={serverSeed.reload} />}

      {saveMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Save failed">
          {formatSaveError(saveMutation.error)}
        </Banner>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={Bot}
          headline="No extractors defined yet"
          description="Add an extractor to make it selectable from a product type's Extraction section."
          action={{ label: '+ New Extractor', onClick: openNewModal }}
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowKey={(row) => row.id}
          expandedRowKey={expandedId ?? undefined}
          renderExpanded={(row) => (
            <ExtractorForm
              id={row.id}
              extractor={row.extractor}
              onChange={(extractor) => updateExtractor(row.id, () => extractor)}
              promptFileOptions={promptFileOptions}
            />
          )}
        />
      )}

      <div className="flex justify-end">
        {(() => {
          const disabledReason = !isDirty
            ? 'No changes to save'
            : serverSeed.baseChanged
              ? STALE_DRAFT_SAVE_REASON
              : hasEmptyModelId
              ? 'Every extractor needs a Model ID before saving'
              : null

          if (disabledReason && !saveMutation.isPending) {
            return (
              <Tooltip text={disabledReason}>
                <button
                  type="button"
                  aria-disabled="true"
                  className="cursor-not-allowed rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  Save
                </button>
              </Tooltip>
            )
          }

          return (
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={handleSave}
              className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )
        })()}
      </div>

      <NewOrDuplicateExtractorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingIds={Object.keys(draft.extractors)}
        source={duplicateSource}
        onConfirm={handleModalConfirm}
      />

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete extractor"
        description={`"${deleteTarget?.id}" will be removed from the draft. Any product type still pointing at it as its extraction extractor will fail validation until repointed. It is only persisted on your next Save.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
